import { Box, Grid as MuiGrid } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { memo, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '~/components/Typography';
import { EmptyState } from '~/components/empty-state';
import CreatePlaylistIllustration from '~/images/illustrations/create-playlist.svg';
import type {
  PlaylistList__Playlist as Playlist,
  PlaylistList__PlaylistFolder as PlaylistFolder,
} from '../../queries/queries.generated';
import { PlaylistGridItem } from './PlaylistGridItem';

export interface PlaylistGridProps {
  playlists: readonly Playlist[];
  folders: readonly PlaylistFolder[];
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
}

export const PlaylistGrid = memo(
  ({ playlists, folders, onCheck, selectedIds }: PlaylistGridProps) => {
    const { t } = useTranslation();

    if (folders.length === 0 && playlists.length === 0) {
      return (
        <EmptyState
          illustration={CreatePlaylistIllustration}
          header={t('pages.playlists.components.empty_state.header')}
          description={t('pages.playlists.components.empty_state.description')}
        />
      );
    }

    return (
      <>
        {folders.length > 0 && (
          <Box
            sx={{
              paddingBottom: 4,
              marginBottom: 4,
              borderBottom: playlists.length > 0 ? 1 : 0,
              borderBottomColor: 'borders.main',
            }}
          >
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              {t('pages.playlists.components.grid.folders_heading')} ({folders.length})
            </Typography>
            <MuiGrid container spacing={2}>
              {folders.map((folder) => (
                <PlaylistGridItem
                  key={`PlaylistFolder-${folder.id}`}
                  item={folder}
                  onCheck={onCheck}
                  selectedIds={selectedIds}
                />
              ))}
            </MuiGrid>
          </Box>
        )}
        {playlists.length > 0 && (
          <Box sx={{ paddingBottom: 4 }}>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              {t('common.pluralize.playlist_plural', { count: 2 })} ({playlists.length})
            </Typography>
            <MuiGrid container spacing={2}>
              {playlists.map((playlist) => (
                <PlaylistGridItem
                  key={playlist.id}
                  item={playlist}
                  onCheck={onCheck}
                  selectedIds={selectedIds}
                />
              ))}
            </MuiGrid>
          </Box>
        )}
      </>
    );
  },
);

PlaylistGrid.displayName = 'PlaylistGrid';
